import {
  HTTPForbiddenError,
  HTTPNotFoundError,
  HTTPRequestError,
  HTTPResponseError,
  HTTPUnauthorizedError,
  HTTPUtils,
  type BaseErrorSubClassInstanceProps,
  type HTTPResponseErrorProps,
} from "@validereinc/utilities";
import { config } from "../../config.ts";
import { type RequestWrapperType, requestWrapper } from "./requestWrapper.ts";
import { type RequestCallbackType } from "./types.ts";

export type GETRequestType = {
  version?: number;
} & Pick<RequestWrapperType, "query" | "endpoint" | "headers" | "responseType">;

type GETProps = {
  api: string;
  requestCallback?: RequestCallbackType;
} & GETRequestType;

/**
 * Make an HTTP GET request. Default response parsing is json. If you need the
 * response object directly, use fetch() instead.
 *
 * @param props - GET request props
 * @returns response body as T
 */
export const GET = async <T = unknown>({
  api,
  endpoint,
  query = {},
  headers = {},
  responseType = "json",
  requestCallback = (response) => response,
}: GETProps): Promise<T> => {
  try {
    const request = requestWrapper({
      method: "GET",
      endpoint: `${api}${endpoint}`,
      query,
      shouldQueryHaveArrayNotation:
        api == config.elixirURL || api === config.alertingURL,
      headers,
    });
    const response = requestCallback
      ? await fetch(request).then(requestCallback)
      : await fetch(request);

    if (HTTPUtils.status.isSuccess(response.status)) {
      switch (responseType) {
        case "arraybuffer":
          return (await response.arrayBuffer()) as T;
        case "blob":
          return (await response.blob()) as T;
        case "json": {
          return (await response.json()) as T;
        }
        default:
          return {} as T;
      }
    } else {
      throw { response, request };
    }
  } catch (cause: unknown) {
    if (!HTTPUtils.objectHasResponseAndRequest(cause)) {
      throw new HTTPRequestError(
        "GET fetch failure or request utility error:",
        {
          method: "GET",
          endpoint: `${api}${endpoint}`,
          requestQuery: query,
          requestContentType: headers["Content-Type"],
          cause,
        }
      );
    }

    // cause excluded since it is a custom object we constructed
    const props: BaseErrorSubClassInstanceProps<HTTPResponseErrorProps> = {
      method: "GET",
      status: cause.response.status,
      endpoint: cause.request.url,
      requestQuery: query,
      requestContentType: headers["Content-Type"],
      responseContentType: cause.response.headers.get("Content-Type"),
    };

    if (
      cause.response.headers.get("Content-Type")?.includes("application/json")
    ) {
      if (HTTPUtils.status.isNotAuthorized(cause.response.status)) {
        throw new HTTPUnauthorizedError("GET unauthorized", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else if (HTTPUtils.status.isForbidden(cause.response.status)) {
        throw new HTTPForbiddenError("GET forbidden", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else if (HTTPUtils.status.isNotFound(cause.response.status)) {
        throw new HTTPNotFoundError("GET resource not found", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      } else {
        throw new HTTPResponseError("GET error with info from API", {
          ...props,
          responseBodyJson: await cause.response.json(),
        });
      }
    }

    switch (cause.response.status) {
      case 401:
        throw new HTTPUnauthorizedError("GET unauthorized", props);
      case 403:
        throw new HTTPForbiddenError("GET forbidden", props);
      case 404:
        throw new HTTPNotFoundError("GET not found", props);
      default:
        throw new HTTPResponseError("GET unknown error", props);
    }
  }
};
